<template>
    <ModalComp
        :title="stationRole.Name"
        :inital-visible="true"
        :close-on-escape="true"
        @closed="onClosed"
        :footer-buttons="buttons"
    >
        <template #content>
            <MatrixStationRoleCellTimeEditor
                v-if="!stationRole.IsShootingStation"
                :station-role="stationRole"
                :clock-time="model!.cell.RaceContestantTimeClockTime"
                :used-time="model!.cell.RaceContestantTimeUsedTime"
                :raceContestantStartTime="model!.row.StartTime"
                :contestant-id="model!.row.ContestantId"
                :race-contestant-id="model!.row.RaceContestantId"
                :editingTuid="model!.cell.RaceContestantTimeTUID"
                :contestantTimeGetter="contestantTimesGetter"
                :StationIdToBackupStationIdsMap="StationIdToBackupStationIdsMap"
                ref="editor"
            />

            <MatrixStationRoleCellShootingEditor
                v-if="stationRole.IsShootingStation"
                :bib="model!.row.Bib"
                :contestant-id="model!.row.ContestantId"
                :race-contestant-id="model!.row.RaceContestantId"
                :station-role-id="model!.cell.StationRoleId"
                :raceContestantStartTime="model!.row.StartTime"
                :editing-tuid="model!.cell.RaceContestantTimeTUID"
                :race-id="model!.row.MatrixConfig.RaceID ?? undefined"
                :prefill-lane="model!.cell.ShootingResult?.Lane"
                :prefill-misses="model?.cell.ShootingResult?.Misses.filter((m) => m)?.length ?? undefined"
                :warnings-acknowledged-by-name="model!.cell.ShootingWarningsAcknowledgedmentByName"
                ref="editor"
            />
        </template>
    </ModalComp>
</template>
<script setup lang="ts">
import { ModalComp } from '@/components';
import { computed } from 'vue';
import { useEventCommonStore, useEventStore, useToastStore, useUserStore } from '@/stores';
import type MatrixStationRoleCell from '../classes/MatrixStationRoleCell';
import MatrixStationRoleCellTimeEditor from './MatrixStationRoleCellEditors/MatrixStationRoleCellTimeEditor.vue';
import MatrixRow from '../classes/MatrixRow';
import { $tm } from '@/locize';
import { ref } from 'vue';
import { ModalButton } from '@/components/Modal/ModalButton';
import MatrixStationRoleCellShootingEditor from './MatrixStationRoleCellEditors/MatrixStationRoleCellShootingEditor.vue';
import { MatrixShootingCellWarningsAcknowledgement, type ContestantTime } from '@/objects/generated';
import type { BackupStationId, ContestantId, StationId } from '@/utilities/typealiases';
import { MatrixDao } from '@/dao';
import { useTaskLoader } from '@/composeables';

const model = defineModel<{ row: MatrixRow; cell: MatrixStationRoleCell } | null>({ required: true });

const props = defineProps<{
    contestantTimesGetter: (contestantId: ContestantId) => ContestantTime[];
    StationIdToBackupStationIdsMap: Map<StationId, BackupStationId[]>;
}>();

const { StationRoles } = useEventCommonStore();

const stationRole = computed(() => {
    const sr = StationRoles.find((sr) => sr.ID === model.value!.cell.StationRoleId);
    if (!sr) {
        throw new Error(`Could not find station role with id ${model.value?.cell.StationRoleId} in MatrixStationRoleCellTimeEditor`);
    }
    return sr;
});

// Checks if there is any shootingwarnings on the cell
// Note this essentially duplicates the logic from MatrixShootingCellRenderer.vue
// Note, this doesnt actually need to be a computed since it doesnt do anything reactive, but its nice to wrap the logic in something
const hasShootingWarnings = computed(() => {
    // If its not shooting, then we cant have warnings
    if (!model.value?.cell.IsShooting) {
        return false;
    }

    // If the warnings have already been acknowledged we dont have warnings anymore
    if (model.value.cell.ShootingWarningsAcknowledgedmentByName) {
        return false;
    }

    // Calculate the misees. Either from the shootingresult or directly from the misses
    const misses = model.value?.cell.ShootingResult?.Misses.filter((m) => m === true).length ?? null;

    // Null means we havent shot yet, so no warnings
    if (misses === null) {
        return false;
    }

    // Check penaltylapcount
    if (model.value?.cell.PenaltyLapCount !== misses) {
        return true;
    }

    // Check backupshooting
    if (model.value?.cell.BackupShootingMisses !== null) {
        if (model.value?.cell.BackupShootingMisses !== misses) {
            return true;
        }
    }

    // No warnings
    return false;
});

const editor = ref<{ save: () => Promise<void>; saveButtonEnabled: () => boolean } | null>(null);

const { AddLoader: AddSaveWarningsLoader, LoadState: WarningsLoadstate } = useTaskLoader();

const buttons = computed(() => [
    // Add acknowledge warnings button if we have warning
    ...(hasShootingWarnings.value
        ? [
              ModalButton.Create({
                  text: $tm('shooting.acknowledgewarnings'),
                  buttonType: 'Warning',
                  loadstate: WarningsLoadstate,
                  action: async () => {
                      const user = useUserStore().user;
                      if (!user) {
                          useToastStore().addError($tm('session.notloggedin'));
                          return;
                      }

                      await AddSaveWarningsLoader(async () => {
                          // Wait for save
                          await MatrixDao.SaveMatrixShootingCellWarningsAcknowledgements(useEventStore().eventID, [
                              MatrixShootingCellWarningsAcknowledgement.FromJsValues({
                                  EntityState: 'Added',
                                  AcknowledgedByName: user.Name,
                                  AcknowledgedByUserId: user.ID,
                                  RaceContestantId: model.value!.row.RaceContestantId,
                                  StationRoleId: model.value!.cell.StationRoleId,
                                  LapNumber: model.value!.cell.LapNumber,
                              }),
                          ]);
                      }, '');

                      // Set the internal ack so we show the ack in the dialog
                      model.value!.cell.ShootingWarningsAcknowledgedmentByName = user.Name;

                      // Close the dialog (this will also hide the ack message we just made sure is updated, but whatever)
                      model.value = null;
                  },
              }),
          ]
        : []),

    // Cancel and Save buttons
    ModalButton.Create({
        text: $tm('button.cancel'),
        buttonType: 'Secondary',
        action: () => {
            model.value = null;
        },
    }),

    ModalButton.Create({
        text: $tm('button.save'),
        buttonType: 'Primary',
        action: async () => {
            // TODO: Both save methods disable the editingTuid. We can move that code here instead of duplicating it.
            await editor.value?.save();
            model.value = null;
        },
        disabled: computed(() => {
            if (editor.value?.saveButtonEnabled() ?? false) {
                return false;
            }
            return true;
        }),
    }),
]);

const onClosed = () => {
    model.value = null;
};
</script>
